// ViewerChrome — Phase 11 Step 4 Block 4 + Step 5 Block 1.
//
// Fullscreen-Viewer für `/s/:slug`. Immer dunkel (unabhängig von Variant),
// Top-Bar (Close + Creator + Mode-Toggle), Right-Rail (Like/Save/Share,
// variant-aware mit Branding-Accent), Colophon (Creator-Block + Tags +
// "Weitere Scrollys"), ESC-Close.
//
// Mode (Step 5 Block 1):
//   - `phone`      → Center-Letterbox mit Phone-Frame, Rail daneben (rechts).
//   - `immersive`  → Content füllt Viewport, Rail floatet als Overlay rechts.
// Der Toggle ist in Block 1 lokaler State (Default `phone`) — Block 2 ersetzt
// das mit einem `useViewerMode()`-Hook (URL + localStorage).
//
// Source-Pfade (Phase 11.5):
//   - `native`   → Platzhalter-Div-Content + Branding-Pipeline an.
//   - `embed`    → Iframe zu `externalUrl` mit Sandbox; Branding-Pipeline aus.
//   - `imported` → Platzhalter-Div wie native, Branding-Pipeline aus.
//
// Optimistic-UI:
//   Like/Save/Share/Follow sind lokaler useState — kein Backend, keine
//   Persistenz. Wenn `loggedIn=false` öffnet Klick ein Sign-in-Modal-Stub.

import { useEffect, useRef, useState } from 'react';
import {
  IconHeart,
  IconBookmark,
  IconShare,
  IconX,
  IconMoreHorizontal,
} from '../common/Icons';
import { PINK } from '../../lib/tokens';
import { formatViews } from '../../lib/format';
import { type MockScrolly } from '../../lib/mock/scrollys';
import { CATEGORY_BY_ID } from '../../lib/categories';
// H1-Phase-1-Track-2-Sub-2.3 (2026-05-18): UpNext-Section aus Colophon
// raus, eigene Component nach Colophon. Source-of-truth: mockup
// docs/mockups/viewer.jsx UpNextInline.
import { UpNextInline } from './UpNextInline';
import type { ViewerMode } from '../../hooks/useViewerMode';
import { useViewerKeyboard } from '../../hooks/useViewerKeyboard';
import {
  useResponsiveViewer,
  ScrollyViewer,
  VIEWER_ROOT_CONTAINER,
  CoverSection,
  viewerT as t,
} from '@scrolly/viewer';
import type { Lang } from '@scrolly/viewer';
// Plan-N2 Block 3 v4 (2026-05-25 Bene): ClientOnly entfernt — app/-
// Pattern direkt SSR-rendern, kein PlaceholderContent-Flash mehr.
import { useResumeState } from '../../hooks/useResumeState';
import { usePublicAuth } from '../../hooks/usePublicAuth';
import { CompanyLogo } from './CompanyLogo';
import { CreditsOverlay } from './CreditsOverlay';
import { Colophon } from '@scrolly/viewer';
import { ScrollIndicatorEcho } from './ScrollIndicatorEcho';
import { VideoDebugOverlay } from './VideoDebugOverlay';
import { useEngagementMutation } from '../../hooks/useEngagementMutation';
import { SignInModal } from '../auth/SignInModal';
import type { EngagementCounts } from '../../lib/api/client';

function readLiveViewportHeight(): number {
  if (typeof window === 'undefined') return 800;
  return window.visualViewport?.height ?? window.innerHeight;
}

// ---- Mode --------------------------------------------------------------
// Phase 11 Step 5: zwei Kern-Modes des Viewers. Phone ist der Step-4-Default
// (Letterbox + Rail daneben). Immersive füllt den Viewport; die Rail floatet
// als Overlay. Die Source-of-Truth für den Typ liegt im Hook — hier nur
// der Re-Export für Konsumenten, die ViewerChrome direkt importieren.

export type { ViewerMode };

// ---- V2 Branding CSS Vars (Phase 13 Commit 13) -------------------------
// The BrandingProvider wrapper (public/src/components/viewer/BrandingProvider.tsx)
// emits these custom properties onto the viewer subtree. Every chrome
// component that previously read the V1 `branding` prop now references
// these with an inline fallback — so the viewer stays readable when no
// provider is mounted (dev paths, storybook, bare component tests).
//
// The fallback colour matches VIEWER_BRANDING_DEFAULTS (platform pink),
// the radius matches the former V1 default (24px), the font stack matches
// what tokensToCssVars produces for DM Sans.
//
// H1-Phase-1.B (2026-05-18): uiFont-Fallback Inter → DM Sans, so dass
// Fallback und Branding-Provider-Output identisch sind (Drift-Elimination).
const BRAND_VAR = {
  accent: 'var(--scrolly-brand-colors-primary, rgba(255, 51, 102, 1))',
  accentContrast: '#111',
  radius: 'var(--scrolly-brand-shape-corner-radius, 24px)',
  uiFont:
    'var(--scrolly-brand-typography-ui-family, "DM Sans", -apple-system, system-ui, sans-serif)',
} as const;

// Stufe-1 i18n: alle Orgs → 'de'. Stufe 2 wird das per-scrolly auflösen
// via scrolly.metadata.lang ?? org.defaultLanguage ?? 'de'.
const LANG: Lang = 'de';

// ---- Auth-State (Sub-Step 3.3) ----------------------------------------
// Vorher: useMockLoggedIn — localStorage-stub für Dev-Browser. Ersetzt
// durch usePublicAuth (echte OIDC-Lesung via react-oidc-context). Während
// SSR + first-render returnt usePublicAuth anonyme defaults, nach
// explizitem User-Sign-In (Heart/Save → SignInModal) gibt es echten State.
// 2026-05-28: AutoSilentSso entfernt (Safari ITP iframe-Banner), kein
// Auto-Carry mehr von portal/ → viewer.
//
// Phase 1 (Sub-Auftrag 2 S2.6): Hook ist da, Behavior unverändert sichtbar
// (loggedIn=false bei anonym, Counter zeigt heute Mock-Werte).
// Phase 2 wird die SideActions erweitern (counts immer sichtbar, anonym
// → SignIn-Modal, auth → Like/Save mit hasLiked/hasSaved-State).

// ---- Hauptkomponente ---------------------------------------------------

export function ViewerChrome({
  scrolly,
  mode: modeProp,
  onToggleMode: onToggleModeProp,
  renderMode,
  chromeMinimal = false,
  initialSceneIdxOneIndexed,
  publicKey,
  engagement,
  initialIsMobile = false,
}: {
  scrolly: MockScrolly;
  /**
   * Plan-N2 Block 3 v3 (2026-05-25 Bene): SSR-UA-Hint für Mobile-
   * Hydration-Flash-Fix. ScrollyViewerPage liest pageContext.viewportHint
   * (gesetzt im onRenderHtml via UA-Sniffing) und passt es hier durch.
   * useResponsiveViewer nimmt's als Initial-State — kein PhoneStage-
   * Flash mehr beim ersten Render auf Mobile.
   */
  initialIsMobile?: boolean;
  /**
   * Sub-Step 3.3 Phase 2: echter publicKey aus dem Backend-Resolver
   * (ResolvedScrolly.publicKey). Brauche ich für POST /like + /save +
   * /view-Endpoint-URLs. Optional weil Mock-Fallback-Pfade (lokales
   * SCROLLYS_BY_SLUG) keinen publicKey haben — dann blocken die
   * Engagement-Aktionen im SideActions silent.
   */
  publicKey?: string;
  /**
   * Sub-Step 3.3 Phase 2: Engagement-State (counts + optional hasLiked/
   * hasSaved). Wird via Prop von ScrollyViewerPage durchgereicht und
   * an SideActions weitergegeben. Bei Mock-Pfaden (kein Backend-Resolve)
   * kann das Feld fehlen — dann zeigt SideActions Nullen.
   */
  engagement?: EngagementCounts;
  /** Optional gesteuerter Mode. Ohne Prop fällt die Komponente auf lokalen
   * State zurück — das ist der Block-1-Pfad. Block 2 wird den Hook
   * (`useViewerMode()`) auf der Page-Ebene mounten und beides hier
   * durchreichen. */
  mode?: ViewerMode;
  onToggleMode?: () => void;
  /** Variante D (Vorbedingungs-Track STOPP D CP2): wenn `"og"` gesetzt
   * ist, blendet ViewerChrome alle UI-Chrome-Elemente aus (TopBar,
   * Colophon, KeyboardHint, SignInStub) und forciert immersive Mode.
   * Wird vom OG-Image-Backend (CP3) via `?renderMode=og` Query-Param
   * gesetzt, damit der Headless-Browser ein sauberes Vollbild rendern
   * kann.
   *
   * 2026-05-29: Plus "embedded" für iframe-Overlay aus portal/. Strippt
   * NUR TopBar (X-Button + Logo kollidieren mit Parent-Overlay-X).
   * SideActions + OverflowActionsMenu bleiben sichtbar — Heart/Save/Share
   * kommen aus iframe-eigener 3-Punkte-Nav statt Overlay-Parallel-Button
   * (Bene-Befund 2026-05-29 nach Polish-Sprint). CompanyLogo/Credits/
   * TitleOverlay bleiben unverändert. */
  renderMode?: 'og' | 'embedded';
  /**
   * Overlay-Stufe-B (2026-06-10): true bei `?chrome=minimal` — das
   * Portal-Overlay rendert Like/Save/Share selbst (same-origin,
   * authentifiziert). Unterdrückt SideActions + embedded-
   * OverflowActionsMenu; Inhalt-Chrome (TitleOverlay, CompanyLogo,
   * Credits) bleibt. Nur sinnvoll zusammen mit renderMode="embedded".
   */
  chromeMinimal?: boolean;
  /**
   * Plan-1.9 Phase 3.B.CP2 — Open-Graph-Anchor 1-indexed Scene-Index aus
   * URL (`#scene-N` oder `?at=scene-N`). Vike-Page parst via
   * `parseSceneAnchor` und reicht das Resultat hierher durch.
   *
   * Konflikt-Logik mit Sub-Phase 3.A Resume-State (Bene-Entscheidung
   * 2026-05-11, siehe docs/PHASE-3-PRE-VERIFIKATION-2026-05-10.md
   * Sub-Phase-3.B-Sektion):
   *
   *   1. Hash gewinnt über Resume — Sharer-Intent ist intentionaler als
   *      impliziter Resume-State.
   *   2. Resume-Toast wird unterdrückt wenn Hash-Anchor da ist (würde
   *      User verwirren — er klickt einen Tweet-Link und sieht
   *      "Weiterlesen ab Slide X"-Toast statt der erwarteten Scene).
   *   3. localStorage-Eintrag bleibt erhalten (kein clearResumeState),
   *      damit der nächste Visit ohne Hash wieder das Resume-Pattern
   *      sehen kann.
   *   4. useResumeState-Hook bleibt aktiv (save-Path persistiert weiter
   *      die Scroll-Position), damit der Hash-Visit selbst zu einem
   *      neuen Resume-State führt.
   */
  initialSceneIdxOneIndexed?: number;
}) {
  const isOg = renderMode === 'og';
  const isEmbedded = renderMode === 'embedded';
  const { loggedIn, signIn } = usePublicAuth();
  const [signInOpen, setSignInOpen] = useState(false);

  // Mode toggle (Phone ↔ Immersive). Kontrollierter Modus via Prop, sonst
  // Fallback auf lokalen State. Vorlage Z. 1473-1477: In-Overlay-Swap,
  // kein Browser-Fullscreen. Bene-Q1: PhoneFrame-Größe wie
  // portal.scrolly.com (responsive 3:4 — aber Vorlage-Look mit
  // Notch/Glow bleibt aus HTML-File).
  const [localMode, setLocalMode] = useState<ViewerMode>('phone');
  const mode = modeProp ?? localMode;
  const onToggleMode =
    onToggleModeProp ??
    (() => setLocalMode((m) => (m === 'phone' ? 'immersive' : 'phone')));

  // Plan-N (2026-05-22): Renderer-Progress kommt jetzt von packages/-
  // ScrollyViewer.onProgress (0..1). Wir multiplizieren zu 0..100 für
  // ProgressHairline.
  const [progress, setProgress] = useState(0);
  const stepProgress = (delta: number) =>
    setProgress((p) => Math.max(0, Math.min(100, p + delta)));

  // iOS-Audio-Permission-Anchor (2026-05-28, re-implementation der
  // ursprünglichen Stage-Gating-Mechanik aus Commit 8daeaba). Vor Plan-N
  // Radikalkur (5fd3d6c) mountete der Renderer erst NACH User-Click.
  // Im Plan-N Refaktor ging das verloren — ScrollyViewer mountet sofort,
  // Renderer initialisiert sich vor User-Activation, erste Video.play()
  // läuft pre-Click → iOS Safari blockt für immer.
  //
  // Fix: chromeHasStarted-State; ScrollyViewer wird erst gemountet wenn
  // chromeHasStarted=true. Pre-Click rendern wir CoverSection direkt in
  // ViewerChrome (ohne Renderer-Mount), Click setzt chromeHasStarted=true,
  // ScrollyViewer mountet im User-Activation-Context, Video.play() läuft.
  //
  // OG-Mode-Auto-Start: Headless-Browser muss Renderer ohne Click
  // mounten, sonst sind alle OG-Image-Screenshots leer.
  const [chromeHasStarted, setChromeHasStarted] = useState(false);
  useEffect(() => {
    if (isOg && !chromeHasStarted) setChromeHasStarted(true);
  }, [isOg, chromeHasStarted]);

  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    if (!chromeHasStarted || progress === 0) return;
    setIsScrolling(true);
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => setIsScrolling(false), 800);
    return () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    };
  }, [progress, chromeHasStarted]);

  useEffect(() => {
    if (!chromeMinimal || typeof window === 'undefined') return;
    try {
      window.parent.postMessage({ type: 'scrolly:scrollState', isScrolling }, '*');
    } catch (_) { /* cross-origin safety */ }
  }, [isScrolling, chromeMinimal]);

  // iOS Safari: after bfcache restore (e.g. back from Unsplash credit) layout
  // dvh and visualViewport diverge → dead strip at the bottom. Re-sync shell
  // height on pageshow only (not on every URL-bar toggle — that stays cached).
  const [liveViewportHeight, setLiveViewportHeight] = useState<number | null>(
    null,
  );
  const [viewportSyncGeneration, setViewportSyncGeneration] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sync = () => {
      setLiveViewportHeight(readLiveViewportHeight());
      setViewportSyncGeneration((g) => g + 1);
    };
    sync();
    window.addEventListener("pageshow", sync);
    return () => window.removeEventListener("pageshow", sync);
  }, []);

  // Pre-START-Scroll-Sperre (2026-06-03): Vor START ist ScrollyViewer NICHT
  // gemountet → der reguläre Scroll-Block (useBlockVertScroll lockt scrollRef)
  // ist inaktiv. Der Body (global.css: min-height:100vh) ist auf iOS höher als
  // der sichtbare Viewport → nativ scrollbar → Cover + Pinned-Overlays (Scrolly-
  // Stern) driften beim Wischen hoch, während das fixed-Chrome korrekt bleibt.
  // Lock html+body-overflow solange das Cover steht; Restore bei START/Unmount.
  // Nicht im OG-Mode (statischer Screenshot, kein Scroll).
  useEffect(() => {
    if (isOg || chromeHasStarted || typeof document === 'undefined') return;
    const html = document.documentElement;
    const { body } = document;
    const prevHtml = html.style.overflow;
    const prevBody = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtml;
      body.style.overflow = prevBody;
    };
  }, [isOg, chromeHasStarted]);

  // Resume-State — Plan-N Bene-Feedback (2026-05-22 22:30): Auto-Resume
  // DEAKTIVIERT. Cover muss IMMER zuerst sichtbar sein, sonst überspringt
  // der Auto-Resume das Cover (Bene-Befund: "Kein Start Button kein
  // Startfeld").
  //
  // localStorage-Save bleibt aktiv (für Plan-2-UX "Continue Reading"-
  // Pattern wenn klar ist wie das aussehen soll). Hash-Anchor (Open-
  // Graph) bleibt funktional — der ist explicit User-Intent.
  const resumePublicKey = scrolly.publicKey ?? '';
  const currentScrollyVersion = scrolly.updatedAt ?? '';
  const { save: saveResumeState } = useResumeState(resumePublicKey);

  // Auto-Resume DEAKTIVIERT — Cover-First-Pattern. localStorage-Read
  // entfernt um sicherzustellen dass Cover IMMER sichtbar ist beim
  // ersten Render.
  const autoScrollPercent: number | undefined = undefined;

  const handleScrollProgress = (percent: number) => {
    if (!resumePublicKey) return;
    saveResumeState(percent, currentScrollyVersion);
  };

  // Keyboard-Shortcuts: Esc → Close, m → Mode-Toggle. Arrows greifen
  // intern im Renderer (scroll-events), nicht via Keyboard-Stub.
  useViewerKeyboard({
    onArrowLeft: () => stepProgress(-10),
    onArrowRight: () => stepProgress(10),
    onEscape: () => handleClose(),
    onModeToggle: onToggleMode,
  });

  // Mobile-Detection — Plan A B.4 konsolidiert via packages/viewer.
  // isCompact (max-width:768px || tablet-portrait) → effectiveMode =
  // immersive. Verhindert Phone-im-Phone auf iPhone.
  // Plan-N2 Block 3 v3: initialIsMobile aus pageContext.viewportHint
  // (UA-Sniffing) als Initial-State — kein Hydration-Flash.
  const { isCompact: isMobileViewport } = useResponsiveViewer(initialIsMobile);
  const effectiveMode: ViewerMode =
    isOg || isMobileViewport ? 'immersive' : mode;

  // scrollRef = funktional rootRef wie app/-PublicSlugViewerPage:165.
  // Wird gleichzeitig viewerRootContainerRef + heightRef + blockScrollElementRef
  // für packages/-ScrollyViewer. Hängt jetzt direkt unter dem Mid-Wrapper
  // (Mobile) bzw. unter dem Phone-Frame (Desktop) — die alten Sub-Components
  // Stage / ScrollyContainer / ScrollyViewerHost sind gelöscht, das spart
  // ~3 Wrapper-Schichten zwischen viewport und Renderer.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const imgproxyUrl = import.meta.env.VITE_IMGPROXY_URL ?? '';

  const isPhone = effectiveMode === 'phone';
  const wide = !isPhone;

  // Iteration 3 (Plan-2 iOS-Jump): body-scroll-auf-iOS verworfen — auf echtem
  // iPhone verursachte es einen iOS-nativen window.scrollY-Reset auf Slide 0
  // beim URL-Bar-Kollaps. Zurück zu container-scroll ÜBERALL. db137a99 hatte
  // body-scroll re-aktiviert → Regression (iPhone-Crash). Revert 2026-06-21.
  const useBodyScroll = false;
  const scrollSource: 'container' | 'window' = 'container';

  // scroll-container Style = app/-rootRef-Pattern. containerType:size +
  // contain:content + containerName verankern die Höhenkette und isolieren
  // den Renderer-Subtree-Layout — verhindert iOS-Compositor-Memory-Spike
  // durch zu viele unnötige Layer.
  // Body-scroll (Plan-2): im window-Mode ist scrollRef KEIN Scroll-Container —
  // die Slides fließen ins Document, der Body scrollt (window.scrollY). scrollRef
  // = Layout-Anker (minHeight:100lvh reserviert das Cover) + Renderer-Container-
  // Context. containerType "inline-size" (NICHT "size") behält die @container
  // (max-width:850px)-Query, ohne Höhen-Containment (das würde height:auto auf 0
  // zwingen + body-scroll brechen). contain:content (paint) raus — würde klippen.
  const scrollContainerStyle: React.CSSProperties = {
    width: '100%',
    height: useBodyScroll ? 'auto' : '100%',
    minHeight: useBodyScroll ? '100lvh' : undefined,
    position: 'relative',
    overflowY: useBodyScroll ? 'visible' : 'hidden',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    contain: useBodyScroll ? undefined : 'content',
    // Body-scroll: KEIN containerType — dessen contain:layout würde scrollRef zu
    // einem Nicht-scrollenden Sticky-Scrollport machen → der sticky WebGL-Canvas
    // klebt am scrollRef statt am Viewport → scrollt weg. Im window-Mode misst der
    // Renderer ohnehin window.innerHeight (nicht die @container-Query); die
    // @container(max-width:850px)-Slide-Stylings werden ggf. via @media gespiegelt.
    containerType: useBodyScroll ? 'normal' : 'size',
    containerName: useBodyScroll ? undefined : VIEWER_ROOT_CONTAINER,
    background: isPhone ? '#fff' : '#000',
  };

  // Renderer-Inhalt. Drei Pfade:
  //
  // (1) Pre-Mount-Cover (scrolly.postJson da, !chromeHasStarted): CoverSection
  //     direkt — ScrollyViewer NICHT gemountet, daher kein Renderer, kein
  //     Video.play() vor User-Click. Click auf Start-Button setzt
  //     chromeHasStarted=true. iOS-Audio-Permission-Anchor.
  //
  // (2) Renderer-Mount (chromeHasStarted=true ODER isOg): ScrollyViewer mit
  //     autoStart={true} (überspringt internes Cover, schon im User-Activation-
  //     Context gemountet). renderCover liefert CoverSection wieder — damit
  //     Cover als position:absolute-Wrapper-Item INNERHALB scrollRef bleibt
  //     und User per Scroll-Up zurück navigieren kann (Bene-iPhone-Befund
  //     2026-05-28). autoStart triggert scrollToStartSlide → User landet
  //     direkt auf slide[0], Cover ist out-of-view aber scroll-erreichbar.
  //
  // (3) Mock-Fallback (kein scrolly.postJson): wie vorher, kein Renderer-Mount
  //     sinnvoll, nur Layout-Stub.
  const innerHeight =
    liveViewportHeight ??
    (typeof window !== 'undefined' ? window.innerHeight : 800);
  const shellHeight =
    liveViewportHeight != null ? `${liveViewportHeight}px` : '100dvh';
  const coverCategory = CATEGORY_BY_ID[scrolly.categoryId];
  const coverEyebrow = coverCategory
    ? `${coverCategory.englishLabel} · A Scrolly`
    : 'Story · A Scrolly';

  const viewerContent = !scrolly.postJson ? (
    // (3) Mock-Fallback ohne Backend-postJson (Feed-Mocks, /__dev__/-Pfade,
    // NotFound-Retry). Nur sichtbar wenn das DB-Scrolly keinen postJson liefert.
    <>
      <CoverSection
        title={scrolly.title}
        description={scrolly.description}
        eyebrowText={coverEyebrow}
        coverImage={scrolly.coverImage}
        onStart={() => {}}
        progress={1}
        isOgMode={isOg}
        height={innerHeight}
        lang={LANG}
      />
      <PlaceholderContent scrolly={scrolly} />
      <AfterContent scrolly={scrolly} wide={wide} />
    </>
  ) : !chromeHasStarted ? (
    // (1) Pre-Mount-Cover. ScrollyViewer wird NICHT gemountet → kein Renderer,
    // kein Video.play(). Click setzt chromeHasStarted=true, ScrollyViewer-
    // Mount erfolgt synchron im User-Activation-Context (Phase 2).
    <CoverSection
      title={scrolly.title}
      description={scrolly.description}
      eyebrowText={coverEyebrow}
      coverImage={scrolly.coverImage}
      onStart={() => setChromeHasStarted(true)}
      progress={1}
      isOgMode={isOg}
      height={innerHeight}
      lang={LANG}
    />
  ) : (
    // (2) Renderer-Mount nach User-Click (oder OG-Auto-Start). autoStart
    // triggert intern scrollToStartSlide → springt zu slide[0], Cover
    // bleibt darüber als scroll-erreichbarer Bereich (zurück per Scroll-Up).
    <ScrollyViewer
      id={scrolly.postJson.data.id ?? scrolly.id}
      post={scrolly.postJson}
      viewerRootContainerRef={scrollRef}
      heightRef={scrollRef}
      blockScrollElementRef={scrollRef}
      scrollSource={scrollSource}
      viewportHeight={liveViewportHeight}
      viewportSyncGeneration={viewportSyncGeneration}
      imgproxyUrl={imgproxyUrl}
      isOgMode={isOg}
      autoStart={!isOg}
      disableCompanyLogo={true}
      onProgress={(p) => setProgress(p * 100)}
      initialScrollPercent={autoScrollPercent}
      onScrollProgress={handleScrollProgress}
      initialSceneIdxOneIndexed={initialSceneIdxOneIndexed}
      renderCover={(controls) => (
        <CoverSection
          title={scrolly.title}
          description={scrolly.description}
          eyebrowText={coverEyebrow}
          coverImage={scrolly.coverImage}
          onStart={controls.onStart}
          progress={controls.progress}
          isOgMode={controls.isOgMode}
          height={controls.height}
          lang={LANG}
        />
      )}
      renderFooter={<AfterContent scrolly={scrolly} wide={wide} />}
    />
  );

  // chromeOverlays (CompanyLogo + CreditsOverlay + ScrollIndicatorEcho)
  // sind Siblings von scroll-container, innerhalb der scrollRef-Box.
  // Pinned-Overlays die nicht mitscrollen.
  // Body-scroll (Plan-2): die Overlays sind position:absolute relativ zur
  // scrollRef-Box. Im window-Mode scrollt der Body → ohne Eingriff würden sie
  // mitscrollen. Wrap in einen position:fixed inset:0 Container (pointerEvents:
  // none, damit Klicks/Scroll durchgehen) → Overlays bleiben viewport-fest.
  const chromeOverlaysRaw = !isOg ? (
    <>
      <CompanyLogo post={scrolly.postJson} />
      <CreditsOverlay post={scrolly.postJson} progress={progress} isOg={isOg} />
      <ScrollIndicatorEcho
        scrollContainerRef={scrollRef}
        scrollSource={scrollSource}
      />
    </>
  ) : null;
  const chromeOverlays =
    chromeOverlaysRaw && useBodyScroll ? (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 40,
        }}
      >
        {chromeOverlaysRaw}
      </div>
    ) : (
      chromeOverlaysRaw
    );

  return (
    <>
      <div
        style={{
          // Plan-N2 Phase 2 Schritt 1 (2026-05-25 Bene): Outer-Container
          // auf app/-Pattern umgestellt. Vorher position:fixed inset:0
          // (Overlay-Pattern aus Vorlage) — das blockte den Body-Scroll und
          // brach die Höhenkette für scrollRef.clientHeight → timer stieg
          // beim Scrollen nicht → alle Layer "in lifetime" → alle Videos
          // spielten gleichzeitig. Plus: Safari iOS URL-Bar Auto-Hide
          // funktionierte nicht weil Safari kein Body-Scroll sah.
          //
          // app/-Pattern (app/src/pages/PublicSlugViewerPage.tsx:163,174):
          //   <div width:100vw height:100dvh overflow:hidden background:#000>
          //
          // 2026-05-28: 100lvh-Quick-Test (d221275) reverted. Schiebte
          // CoverSection-Bottom (Start-Button) unter iOS-Safari-URL-Bar
          // → Button nicht klickbar. URL-Bar-Auto-Hide-Strategie wandert
          // in Plan-2 (Body-Scroll-Architektur, siehe Memory
          // url_bar_auto_hide_plan_2).
          position: 'relative',
          width: '100vw',
          // Body-scroll (Plan-2): im window-Mode darf der Body scrollen — KEIN
          // height:100dvh + overflow:hidden (klippt + verhindert body-scroll).
          // minHeight:100dvh hält den Viewer mindestens viewport-hoch.
          height: useBodyScroll ? 'auto' : shellHeight,
          minHeight: useBodyScroll ? '100dvh' : shellHeight,
          overflow: useBodyScroll ? 'visible' : 'hidden',
          // Plan-N3 Stufe A (Bene 2026-05-27): contain:content macht den
          // Viewer-Subtree zum Layout-Isolation-Punkt. Verhindert dass eine
          // Resize-Notification (z.B. iOS URL-Bar-Movement) den gesamten
          // Page-DOM neu rechnet. app/-PublicSlugViewerPage hat dieses
          // Property im mid-Wrapper (Z. 164), public/ hatte es gar nicht.
          contain: useBodyScroll ? undefined : 'content',
          background: effectiveMode === 'immersive' ? '#000' : '#111',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: BRAND_VAR.uiFont,
        }}
      >
        <style>{`@keyframes stageFadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>

        {/* Page-Level Chrome (Vorlage Z. 1431-1471 + 1479-1489) */}
        {/* Plan-N2 Block 1.b (2026-05-25 Bene): ProgressHairline komplett raus.
         * Verbatim: "rechten Scrollbalken viel zu breit". Auch bei letztem
         * Iteration: "kann ich nichts anfangen". Scroll-Progress ist
         * implicit über die Slides selbst sichtbar. */}

        {/* TopBar (X) / non-embedded OverflowActionsMenu (⋯) / SignInModal sind
         * ans Return-Ende gehoben — AUSSERHALB des contain:content-Outer-Div,
         * sonst klebt ihr position:fixed am Outer-Div statt am Viewport und
         * driftet beim Scrollen (Chrome-Scroll-Fix 2026-06-03). */}

        {/* Stage-Area — Plan-N3 Refaktor (Bene 2026-05-27):
         * Stage / ScrollyContainer / ScrollyViewerHost als Sub-Components
         * gelöscht. Hier inline-gerendert. Mobile = flat app/-3-tier-Pattern
         * (mid + scrollRef + ScrollyViewer). Desktop-Phone = Letterbox-
         * Decoration zusätzlich, scrollRef hängt innerhalb des Frames.
         * Beide Varianten teilen viewerContent + chromeOverlays + scroll-
         * containerStyle (definiert vor return). */}
        {isPhone ? (
          // Desktop Phone-Mode: Letterbox-Frame-Decoration + 9:16 Mockup.
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 20,
              position: 'relative',
              background: `radial-gradient(ellipse at center, ${rgbaFromHex(scrolly.creator.avatar || '#FF3366', 0.25)}, transparent 60%)`,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: 'min(380px, 100%)',
                aspectRatio: '9 / 16',
                maxHeight: 'min(calc(100vh - 120px), 88vh)',
                borderRadius: 36,
                background: '#000',
                boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
                position: 'relative',
                padding: 4,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: 32,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <div
                  ref={scrollRef}
                  className="scrolly-scroll-container"
                  style={scrollContainerStyle}
                >
                  {viewerContent}
                </div>
                {chromeOverlays}
              </div>
            </div>
          </div>
        ) : (
          // Mobile / Immersive: flat app/-Pattern. mid (contain:content) →
          // scrollRef (containerType:size + contain:content). KEIN Phone-
          // Frame, KEINE ScrollyContainer-wrap, KEIN ScrollyViewerHost.
          <div
            style={{
              // Body-scroll (Plan-2): natürliche Höhe (kein flex:1/minHeight:0/
              // contain) → die Slides machen den Wrapper hoch → Body scrollt.
              flex: useBodyScroll ? 'none' : 1,
              minHeight: useBodyScroll ? undefined : 0,
              position: 'relative',
              contain: useBodyScroll ? undefined : 'content',
              width: '100%',
              background: '#000',
            }}
          >
            <div
              ref={scrollRef}
              className="scrolly-scroll-container"
              style={scrollContainerStyle}
            >
              {viewerContent}
            </div>
            {chromeOverlays}
          </div>
        )}

        {/* SideActions floating-rail right (Vorlage Z. 1479-1489) — nur Desktop.
         * Mobile-Variante ist OverflowActionsMenu (3-Dot oben rechts).
         * Plan-N2 Block 3 v3 (2026-05-25 Bene). */}
        {/* 2026-05-29 Polish-Subset-Revert: SideActions im embedded-Mode
         * wieder anzeigen. Bene-Vorgabe: Heart/Save/Share kommen aus
         * iframe-eigener 3-Punkte-Nav statt parallel-gebautem Overlay-
         * Share-Button. !isEmbedded-Gate entfernt. */}
        {!isOg && !chromeMinimal && (
          <SideActions
            scrolly={scrolly}
            loggedIn={loggedIn}
            onRequireSignIn={() => setSignInOpen(true)}
            publicKey={publicKey}
            engagement={engagement}
            isCompact={isMobileViewport}
            isScrolling={isScrolling}
          />
        )}

        {/* OverflowActionsMenu — Mobile-3-Dot-Menu. Die EMBEDDED-Variante
         * (unten-rechts, position:absolute) bleibt hier INNERHALB des Outer-Div
         * unangetastet. Die NON-embedded-Variante (oben-rechts) ist ans
         * Return-Ende gehoben (Chrome-Scroll-Fix — sonst driftet sie). */}
        {!isOg && isMobileViewport && isEmbedded && !chromeMinimal && (
          <OverflowActionsMenu
            isEmbedded={isEmbedded}
            scrolly={scrolly}
            loggedIn={loggedIn}
            onRequireSignIn={() => setSignInOpen(true)}
            publicKey={publicKey}
            engagement={engagement}
          />
        )}

        {/* Title-Overlay Immersive-only bei scroll<0.01 (Vorlage Z. 1492-1516).
         * Plan-N2 Block 3 (2026-05-25 Bene): Threshold von <5% auf <1%
         * gesenkt. Auf Mobile ist effectiveMode IMMER immersive
         * (isCompact-Forcing, Z. 212-213) — der bisherige 5%-Threshold
         * hat den Title-Overlay sichtbar gehalten bis weit in den ersten
         * Slide rein, was wie eine Cover↔Slide-Doppelung aussah (Cover
         * zeigt Titel groß unten, TitleOverlay zeigt Titel groß oben).
         * 1% macht das Overlay nur am allerersten Frame sichtbar (Cover
         * zeigt selbst den Titel), Übergang ist nahezu unsichtbar.
         *
         * Stage-Gating-Anchor (2026-05-28): zusätzlich an chromeHasStarted
         * koppeln. Vor User-Click ist ScrollyViewer NICHT gemountet → kein
         * onProgress-Callback → progress=0 permanent → TitleOverlay wäre
         * IMMER sichtbar parallel zum CoverSection (Eyebrow "Story · Scrolly"
         * vs Cover-Eyebrow "Story · A Scrolly", Bene-Befund 2026-05-28).
         * Mit chromeHasStarted-Gate: TitleOverlay nur post-Click, also
         * während echtem Renderer-Load (progress<1) — Cover war schon weg. */}
        {chromeHasStarted &&
          !isOg &&
          effectiveMode === 'immersive' &&
          progress < 1 && <TitleOverlay scrolly={scrolly} />}

        {/* Plan-N2 (2026-05-25 Bene-Reduktion): KeyboardHint entfernt.
         * Verbatim-Quote: "Die Erklärung is unwichtig die können wir wieder
         * rausnehmen." Keyboard-Shortcuts laufen weiterhin via
         * useViewerKeyboard — nur die UI-Pille unten ist weg. */}

        {/* Diagnose-Overlay (nur sichtbar bei ?videodebug=1) */}
        <VideoDebugOverlay />
      </div>

      {/* Chrome-Scroll-Fix (2026-06-03): TopBar (X) + SignInModal AUSSERHALB
       * des contain:content-Outer-Div als Fragment-Geschwister, damit ihr
       * position:fixed am echten Viewport klebt (Ahnenkette: BrandingProvider
       * →#root→body, kein contain/transform).
       *
       * Bene 2026-06-13: Das non-embedded Mobile-3-Punkte-Menü (⋯) ist hier
       * entfernt — Engagement zeigt auf Mobile jetzt die Einzel-Leiste
       * (SideActions, oben gerendert), wie das Platform-Overlay. Die EMBEDDED-
       * Variante des ⋯-Menüs (oben, im Outer-Div) bleibt für den iframe-Pfad. */}
      {!isOg && !isEmbedded && <TopBar />}
      {!isOg && (
        <SignInModal
          open={signInOpen}
          onClose={() => setSignInOpen(false)}
          onConfirmSignIn={() => {
            signIn(window.location.href);
          }}
        />
      )}
    </>
  );
}

// ============================================================================
// TitleOverlay — Immersive-only Hero-Title bei scroll<0.05 (Vorlage Z. 1492-1516)
// ============================================================================
function TitleOverlay({ scrolly }: { scrolly: MockScrolly }) {
  const category = CATEGORY_BY_ID[scrolly.categoryId];
  const eyebrowText = category
    ? `${category.englishLabel} · Scrolly`
    : 'Story · Scrolly';
  return (
    <div
      style={{
        position: 'absolute',
        left: 40,
        bottom: 40,
        zIndex: 15,
        maxWidth: 560,
        pointerEvents: 'none',
        animation: 'stageFadeIn 400ms',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: PINK,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        {eyebrowText}
      </div>
      <h1
        style={{
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontSize: 56,
          fontWeight: 500,
          color: '#fff',
          margin: 0,
          letterSpacing: -1.2,
          lineHeight: 1.0,
          textShadow: '0 2px 20px rgba(0,0,0,0.5)',
        }}
      >
        {scrolly.title}
      </h1>
      <div
        style={{
          marginTop: 20,
          fontSize: 13,
          color: 'rgba(255,255,255,0.7)',
        }}
      >
        {t('cover.scrollToBegin', LANG)}
      </div>
    </div>
  );
}

// Helper: rgba from hex (für radial-gradient bg in PhoneStage).
function rgbaFromHex(hex: string, alpha: number): string {
  const sanitized = hex.replace('#', '');
  if (sanitized.length !== 6 && sanitized.length !== 3) {
    return `rgba(255, 51, 102, ${alpha})`;
  }
  const expanded =
    sanitized.length === 3
      ? sanitized
          .split('')
          .map((c) => c + c)
          .join('')
      : sanitized;
  const r = parseInt(expanded.substring(0, 2), 16);
  const g = parseInt(expanded.substring(2, 4), 16);
  const b = parseInt(expanded.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ---- KeyboardHint ------------------------------------------------------
// Dezenter Hinweis unten mittig. Zeigt die Shortcut-Liste für 3s,
// blendet dann aus. Mouse-Move (Hover-Intent) setzt den Timer zurück
// und blendet wieder ein. `prefers-reduced-motion` deaktiviert den
// Opacity-Fade — der Hint wechselt dann instantan zwischen sichtbar
// und unsichtbar.

// ---- Stage -------------------------------------------------------------
// Kapselt den Content-Bereich zwischen TopBar und Colophon. Je nach Mode:
//   - phone:     3-Spalten-Grid mit Letterbox zentriert, Rail rechts daneben
//   - immersive: Content füllt den Viewport (abzüglich TopBar-Höhe), Rail
//                floatet als Absolute-Overlay rechts vertikal zentriert.

// ============================================================================
// Bene-Regel (2026-06-13): X führt IMMER auf die Platform — außer der Viewer
// wurde von einer ECHTEN Vorgängerseite geöffnet, dann zurück dorthin. Die
// Platform-Origin ist im Viewer-Client ohnehin hart auf platform.scrolly.online
// verdrahtet (usePublicAuth:82, OidcAuthProvider:67, allowed-return-domains) —
// wir nutzen denselben Wert. Folge: der Beta-Viewer schickt X (wie der
// Beta-Login heute schon) auf die Prod-Platform — konsistent, kein Sonderfall.
const PLATFORM_ORIGIN = 'https://platform.scrolly.online';

function handleClose() {
  if (typeof window === 'undefined') return;
  // (1) Aus dem Portal via window.open geöffnet (mit opener) → Tab schließen
  //     kehrt zur öffnenden Seite zurück.
  if (window.opener) {
    window.close();
    return;
  }
  // (2) Referrer-Härtung: nur „zurück", wenn es eine ECHTE Vorgängerseite in
  //     diesem Tab gab (document.referrer gesetzt UND History-Tiefe > 1).
  //     Direkt-Links (WhatsApp/Messenger, getippte URL) haben leeren Referrer —
  //     auch wenn manche In-App-Browser mit history.length=2 (leere Vorseite)
  //     starten. So landet ein geteilter Link verlässlich auf der Platform
  //     statt auf einer leeren Back-Seite (Bene-Befund 2026-06-13).
  let hasRealReferrer = false;
  try {
    hasRealReferrer =
      !!document.referrer && new URL(document.referrer).origin !== 'null';
  } catch {
    hasRealReferrer = false;
  }
  if (hasRealReferrer && window.history.length > 1) {
    window.history.back();
    return;
  }
  // (3) Direkt-Link / keine Vorgängerseite → die Platform.
  window.location.href = PLATFORM_ORIGIN;
}

// ---- Shared styles ------------------------------------------------------
const iconButtonStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.08)',
  color: '#fff',
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

// ============================================================================
// TopBar — Plan-N2 Bene-Reduktion (2026-05-25): minimal, nur Close-X.
// Creator-Card, Folgen-Button, Share, Mode-Toggle, More — alle weg.
// Fokus auf Story-Content, keine UI-Unruhe.
// ============================================================================
function TopBar() {
  return (
    <div
      style={{
        // Chrome-Scroll-Fix (2026-06-03): fixed statt absolute. Das Chrome ist
        // ans Return-Ende (außerhalb contain:content-Outer-Div) gehoben, daher
        // fixiert sich fixed am visual Viewport → klebt beim Scrollen, driftet nicht.
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 15,
        display: 'flex',
        alignItems: 'center',
        // Fix G (Safe-Area Top): Hintergrund-Gradient zieht bis top:0 unter den
        // Notch, der X-Button sitzt darunter. Fallback 0px → Non-Notch unverändert.
        padding: 'calc(env(safe-area-inset-top, 0px) + 20px) 28px 20px',
        // Sanfter Gradient, dezent (vorher rgba(0,0,0,0.6))
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.35), transparent)',
        pointerEvents: 'none',
      }}
    >
      <button
        type="button"
        onClick={handleClose}
        aria-label={t('viewer.close', LANG)}
        style={{ ...iconButtonStyle, pointerEvents: 'auto' }}
      >
        <IconX size={20} />
      </button>
    </div>
  );
}

// Sprint-2 helper: bündelt Colophon + UpNextInline für afterContentSlot.
// Mockup-Source: docs/mockups/viewer/Scrolly_Platform-2.html:1591-1601
// (ScrollyContent rendert sections → Colophon → UpNextInline).
//
// Block 2.6 P1 (2026-05-22): umbenannt PhoneAfterContent → AfterContent,
// nimmt `wide`-Prop. Wird in Phone-Mode (wide=false) UND Immersive-Mode
// (wide=true) verwendet. Vorlage Z. 1591-1601: ScrollyContent rendert
// sections + Colophon + UpNextInline; `wide` wird durch Phone/Immersive
// gesetzt.
function AfterContent({
  scrolly,
  wide,
}: {
  scrolly: MockScrolly;
  wide: boolean;
}) {
  // Plan-N2 Block 3 (2026-05-25 Bene): Mobile-Schmalviewport-Detection
  // direkt im AfterContent. useResponsiveViewer ist ein einfacher
  // matchMedia-Hook, mehrfach-Aufruf ist nebenwirkungsfrei (kein State
  // außer dem media-query-listener). Vermeidet Prop-Drilling via
  // ScrollyContainer/ScrollyViewerHost, das wäre 3 Layer tief.
  const { isCompact } = useResponsiveViewer();

  const post = scrolly.postJson?.data as
    | { metadata?: { credits?: string; sources?: string; contact?: string } }
    | undefined;
  const metadata = post?.metadata ?? null;

  // Block 2.5 TEIL B (2026-05-22): Inline-Colophon (Profil-Karte mit
  // Avatar/Follow-Button/Bio + Lese-Fortschritt-Notice) entfällt; ersetzt
  // durch Vorlage-konforme ColophonSubtle/ColophonBold aus Colophon.tsx.
  // StoryFooter.tsx ebenfalls entfällt (war alter "powered by scrolly"-
  // Baustein, nicht Vorlage). Abbinder ist Teil des neuen Colophon.
  //
  // Block 2.5 TEIL C (2026-05-22): UpNextInline-Items-Refactor.
  // VORHER: UpNextInline las ALL_SCROLLYS-Mock (Dummy-Daten).
  // JETZT:  items={[]} — Rail wird heute ausgeblendet weil kein
  //         Public-Listing-Endpoint für Org-Nachbarn existiert.
  // Briefing 2026-05-22 (BRIEFING-BLOCK-2.5-END-SEQUENZ-UND-FULLSCREEN.md
  // TEIL C): "Wenn keine Nachbarn vorhanden: Rail leer lassen oder
  // ausblenden, NICHT mit Dummy füllen." Plan-2-Backlog: Public-Endpoint
  // `GET /public/scrollys/by-org/:orgSlug` + `useOrgNachbarn`-Hook
  // (siehe UpNextInline.tsx Header-Comment).
  return (
    // Fix (2026-06-13): Der Renderer-Sticky-Canvas (zIndex 998) + die fixed
    // Credits (zIndex 1200, packages/renderer VisualStoryPlayer) liegen im
    // SELBEN Wrapper-Stacking-Context wie diese Fußzeile. Ohne eigenen z-index
    // (= auto/0) malt das eingefrorene letzte Renderer-Frame am Story-Ende ÜBER
    // die Colophon-Karte ("Riesen-cr überlappt End.", Bene 2026-06-13). Gleiche
    // Klasse wie der CompanyLogo-z-2000-Fix (siehe lib/z-index.ts:30-38). 1300
    // deckt 998+1200 ab, bleibt unter Logo (2000) und Modal (10000).
    <div style={{ position: 'relative', zIndex: 1300 }}>
      <Colophon
        creatorName={scrolly.creator.name}
        publishedAt={scrolly.publishedAt}
        duration={scrolly.stats.duration}
        views={scrolly.stats.views ? String(scrolly.stats.views) : undefined}
        credits={metadata?.credits}
        sources={metadata?.sources}
        contact={metadata?.contact}
        orgContact={scrolly.orgContact}
        legalLinks={scrolly.legalLinks}
        wide={wide}
        isCompact={isCompact}
        style="subtle"
        lang={LANG}
      />
      <UpNextInline items={[]} wide={wide} lang={LANG} />
    </div>
  );
}

function PlaceholderContent({ scrolly }: { scrolly: MockScrolly }) {
  const label =
    scrolly.source === 'imported'
      ? `Imported Scrolly · ${scrolly.importedFrom ?? 'Externe Quelle'}`
      : 'Scrolly-Content rendert hier';
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
        color: 'rgba(255,255,255,0.72)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.5)',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 500,
          color: '#fff',
          lineHeight: 1.3,
        }}
      >
        {scrolly.title}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'rgba(255,255,255,0.5)',
          marginTop: 16,
          maxWidth: 280,
        }}
      >
        Der eigentliche Renderer folgt in einer späteren Phase. Der Block-4-
        Chrome zeigt Letterbox, Rail und Colophon-Gerüst.
      </div>
    </div>
  );
}

// ---- SideActions (Floating) --------------------------------------------
// Sprint-2-Revision Sub-Step 3.4: ehemals RightRail (Sidebar-Layout im
// 3-Spalten-Grid neben PhoneFrame). Mockup-Architektur ist Floating-Rail
// absolute auf Page-Level rechts-unten — siehe
// docs/mockups/viewer/Scrolly_Platform-2.html:1479-1489 (Right side
// reactions) + 1529-1543 (ReactionBtn).
//
// Visual-Spec:
// - position absolute right:28 bottom:40 z:15
// - flex-column, gap:14, alignItems:center
// - 52×52 round Buttons mit rgba(255,255,255,0.12) BG + backdrop-blur 14px
// - Count below button: 11px weight 600 white text-shadow
// - Active-Colors: Pink für Heart, Yellow #FBBF24 für Bookmark
//
// Sub-Step 3.3 Phase 2: Counter immer sichtbar (auch anonym). Heart/
// Bookmark-State kommt aus `engagement.hasLiked`/`hasSaved` (nur bei
// auth-Request gesetzt). Anonyme User: Outlined-Icons + Click triggert
// onRequireSignIn (SignInModal). Authenticated User: Filled-Icons wenn
// liked/saved, Click toggled via useEngagementMutation mit optimistic
// update + rollback bei Fail.

function SideActions({
  scrolly,
  loggedIn,
  onRequireSignIn,
  publicKey,
  engagement,
  isCompact,
  isScrolling,
}: {
  scrolly: MockScrolly;
  loggedIn: boolean;
  onRequireSignIn: () => void;
  publicKey?: string;
  engagement?: EngagementCounts;
  isCompact?: boolean;
  isScrolling?: boolean;
}) {
  // useEngagementMutation: lokaler State (initial aus engagement-Prop)
  // + Toggle-Funktionen für authentifizierte User. No-op-Toggles wenn
  // !loggedIn — Modal-Trigger wird oben im onClickAction-Handler gemacht.
  // useRef-Guard (Phase-1-Lesson): inFlightRef im Hook verhindert
  // konkurrierende Mutations bei Doppel-Klick.
  const mutation = useEngagementMutation({
    publicKey: publicKey ?? '',
    initialLiked: engagement?.hasLiked ?? false,
    initialSaved: engagement?.hasSaved ?? false,
    initialLikesCount: engagement?.likesCount ?? 0,
    initialSavesCount: engagement?.savesCount ?? 0,
  });

  // Counter werden IMMER angezeigt (Sub-Auftrag-Brief Strategy-Decision).
  // Fallback "0" wenn engagement-Field fehlt (mock-Pfade ohne Backend).
  const likeCount = mutation.state.likesCount;
  const saveCount = mutation.state.savesCount;
  // Anonyme User sehen mock-fallback aus `scrolly.stats` wenn engagement
  // fehlt — sonst die echten Backend-Counts (auch 0).
  const showLikeCount =
    engagement !== undefined ? likeCount : (scrolly.stats?.likes ?? 0);
  const showSaveCount =
    engagement !== undefined ? saveCount : (scrolly.stats?.saves ?? 0);

  const onLikeClick = () => {
    if (!loggedIn) return onRequireSignIn();
    void mutation.toggleLike();
  };
  const onSaveClick = () => {
    if (!loggedIn) return onRequireSignIn();
    void mutation.toggleSave();
  };

  // Bene 2026-06-13: Mobile zeigt jetzt dieselbe Einzel-Leiste wie das
  // Platform-Overlay (Herz/Merken/Teilen mit Zählern) — statt des bisherigen
  // 3-Punkte-Menüs (kehrt die Plan-N2-Block-3-v3-Entscheidung vom 25.05. um).
  // Die Rechts/Unten-Platzierung kollidiert nicht mit Cover-Titel/START (beide
  // links). Auf Mobile wird zIndex über die Renderer-Layer (998/1200) gehoben,
  // sonst verschwindet die Leiste hinter Slide-Inhalten — gleiche Lektion wie
  // CompanyLogo z-2000 / Colophon z-1300 (lib/z-index.ts:30-38).
  return (
    <div
      style={{
        position: 'absolute',
        right: isCompact ? 12 : 28,
        bottom: isCompact
          ? 'calc(env(safe-area-inset-bottom, 0px) + 24px)'
          : 40,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        alignItems: 'center',
        zIndex: isCompact ? 1300 : 15,
        transform: isCompact && isScrolling ? 'scale(0.65)' : 'scale(1)',
        opacity: isCompact && isScrolling ? 0.35 : 1,
        transformOrigin: 'bottom right',
        transition: 'transform 250ms ease, opacity 250ms ease',
      }}
    >
      <ReactionBtn
        aria-label={
          mutation.state.liked ? t('viewer.unlike', LANG) : t('viewer.like', LANG)
        }
        aria-pressed={mutation.state.liked}
        active={mutation.state.liked}
        activeColor={BRAND_VAR.accent}
        onClick={onLikeClick}
        icon={<IconHeart size={22} filled={mutation.state.liked} />}
        count={formatViews(showLikeCount)}
      />
      <ReactionBtn
        aria-label={
          mutation.state.saved ? t('viewer.unsave', LANG) : t('viewer.save', LANG)
        }
        aria-pressed={mutation.state.saved}
        active={mutation.state.saved}
        activeColor="#FBBF24"
        onClick={onSaveClick}
        icon={<IconBookmark size={22} />}
        count={formatViews(showSaveCount)}
      />
      <ReactionBtn
        aria-label={t('viewer.share', LANG)}
        active={false}
        onClick={() => {
          const url = window.location.href;
          // Mobile bevorzugt das native Share-Sheet (wie das Platform-Overlay);
          // Fallback Clipboard auf Desktop / ohne Web-Share-API.
          if (
            typeof navigator !== 'undefined' &&
            typeof navigator.share === 'function'
          ) {
            void navigator
              .share({ title: scrolly.title ?? 'Scrolly', url })
              .catch(() => undefined);
            return;
          }
          if (typeof navigator !== 'undefined' && navigator.clipboard) {
            navigator.clipboard.writeText(url).catch(() => undefined);
          }
        }}
        icon={<IconShare size={22} />}
        // Label unter Share-Icon (kein Engagement-Counter). Sub-Step
        // 3.6.5 Befund-3-Fix.
        count="Share"
      />
    </div>
  );
}

// ============================================================================
// OverflowActionsMenu — Mobile-Variante der SideActions
// ============================================================================
//
// Plan-N2 Block 3 v3 (2026-05-25 Bene): 3-Punkte-Menu oben rechts statt
// Bottom-Bar. Reduziert die Mobile-Chrome auf den absoluten Minimum
// (Close-X links, Logo Mitte, ⋯ rechts). Engagement-Aktionen wandern
// hinter 1 Tap — passt zur Reduktions-Linie der ganzen Block-Reihe und
// matched iOS-Standard-Pattern (Mail/Notes/Safari-Reader).
//
// Auth/Mutation-Logic ist 1:1 von SideActions kopiert — useEngagement-
// Mutation, onLikeClick → SignInModal-Trigger wenn !loggedIn, etc.
// Beide Komponenten werden nie gleichzeitig gemountet (conditional
// render in ViewerChrome auf Basis isMobileViewport), also keine
// State-Sync-Issues.
//
// UX-Details:
//   - Button: 44×44 Touch-Target, IconMoreHorizontal (3 Punkte horiz)
//   - Dropdown öffnet bei Klick, schließt bei Outside-Click + Escape
//   - Menu-Items haben Icon + Label nebeneinander, Mockup-konform
//     (kein Tippen-müssen, klare Affordances)
//   - Active-States: Heart pink wenn liked, Bookmark yellow wenn saved
//   - z-Index 16 (über TopBar=15, unter SignInModal=10000)
function OverflowActionsMenu({
  scrolly,
  loggedIn,
  onRequireSignIn,
  publicKey,
  engagement,
  isEmbedded = false,
}: {
  scrolly: MockScrolly;
  loggedIn: boolean;
  onRequireSignIn: () => void;
  publicKey?: string;
  engagement?: EngagementCounts;
  /**
   * Phase-2-Overlay (2026-05-29): wenn true, wandert der 3-Punkte-Button
   * von oben rechts nach UNTEN rechts (= weg von Overlay-eigenem
   * Close-X). Dropdown öffnet dann nach OBEN. Default false (Top-Right
   * für Standalone-Viewer auf viewer.scrolly.com).
   */
  isEmbedded?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const mutation = useEngagementMutation({
    publicKey: publicKey ?? '',
    initialLiked: engagement?.hasLiked ?? false,
    initialSaved: engagement?.hasSaved ?? false,
    initialLikesCount: engagement?.likesCount ?? 0,
    initialSavesCount: engagement?.savesCount ?? 0,
  });

  // Outside-Click + Escape schließen das Menu. Cleanup bei
  // open=false (kein Listener wenn unsichtbar). Pointer-Events deckt
  // Maus+Touch+Pen einheitlich ab — sicherer als Mouse/Touch getrennt.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: Event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  const close = () => setOpen(false);

  const onLikeClick = () => {
    close();
    if (!loggedIn) return onRequireSignIn();
    void mutation.toggleLike();
  };
  const onSaveClick = () => {
    close();
    if (!loggedIn) return onRequireSignIn();
    void mutation.toggleSave();
  };
  const onShareClick = () => {
    close();
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard
        .writeText(window.location.href)
        .catch(() => undefined);
    }
  };

  // Counter werden im Dropdown rechts angezeigt (kompakt, nur wenn
  // Backend-Engagement vorhanden — Mock-Pfade ohne publicKey haben
  // keine echten Counts und blenden das Label aus).
  const showLikeCount =
    engagement !== undefined
      ? mutation.state.likesCount
      : (scrolly.stats?.likes ?? 0);
  const showSaveCount =
    engagement !== undefined
      ? mutation.state.savesCount
      : (scrolly.stats?.saves ?? 0);

  // 2026-05-29 isEmbedded: Position-Switch von oben-rechts auf unten-rechts
  // damit der Button nicht mit dem Overlay-eigenen Close-X (oben rechts)
  // kollidiert. safe-area-inset-bottom respektiert iOS-Home-Indicator.
  const wrapperStyle: React.CSSProperties = isEmbedded
    ? {
        position: 'absolute',
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
        right: 16,
        zIndex: 2050,
      }
    : {
        // Chrome-Scroll-Fix (2026-06-03): fixed statt absolute — die non-embedded
        // Variante ist ans Return-Ende (außerhalb contain:content) gehoben, daher
        // klebt fixed am visual Viewport. Embedded (oben) bleibt absolute.
        position: 'fixed',
        // Fix G (Safe-Area Top): 3-Punkte-Button unter den Notch schieben
        // (spiegelt das Bottom-Safe-Area-Muster). Fallback 0px → Non-Notch wie bisher.
        top: 'calc(env(safe-area-inset-top, 0px) + 20px)',
        right: 16,
        // Plan-N2 Block 3 v3: über CompanyLogo (z-2000) und unter
        // SignInModal (z-10000). CompanyLogo ist heute zentriert (Position
        // 'M' für Scrollytelling GmbH), auf anderen Orgs könnte 'R'/right
        // gepflegt sein und dann visuell mit dem 3-Dot-Button kollidieren.
        // Höherer z-Index sichert dass der Button immer obenauf sitzt.
        zIndex: 2050,
      };

  return (
    <div ref={wrapperRef} style={wrapperStyle}>
      <button
        type="button"
        aria-label={t('viewer.actions', LANG)}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          ...iconButtonStyle,
          pointerEvents: 'auto',
        }}
      >
        <IconMoreHorizontal size={20} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label={t('viewer.actions', LANG)}
          style={{
            position: 'absolute',
            // 2026-05-29 isEmbedded: Dropdown öffnet nach oben statt unten
            // (Button sitzt unten rechts, sonst würde Menu unter Viewport).
            ...(isEmbedded ? { bottom: 48 } : { top: 48 }),
            right: 0,
            minWidth: 220,
            background: 'rgba(20,20,22,0.92)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            borderRadius: 14,
            padding: 6,
            boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            fontFamily: 'inherit',
          }}
        >
          <OverflowMenuItem
            icon={<IconHeart size={20} filled={mutation.state.liked} />}
            label={mutation.state.liked ? t('viewer.liked', LANG) : t('viewer.like', LANG)}
            count={formatViews(showLikeCount)}
            active={mutation.state.liked}
            activeColor={BRAND_VAR.accent}
            onClick={onLikeClick}
          />
          <OverflowMenuItem
            icon={<IconBookmark size={20} />}
            label={mutation.state.saved ? t('viewer.saved', LANG) : t('viewer.save', LANG)}
            count={formatViews(showSaveCount)}
            active={mutation.state.saved}
            activeColor="#FBBF24"
            onClick={onSaveClick}
          />
          <OverflowMenuItem
            icon={<IconShare size={20} />}
            label={t('viewer.share', LANG)}
            count={null}
            active={false}
            onClick={onShareClick}
          />
        </div>
      )}
    </div>
  );
}

function OverflowMenuItem({
  icon,
  label,
  count,
  active,
  activeColor,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  count: string | null;
  active: boolean;
  activeColor?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: 'transparent',
        border: 'none',
        borderRadius: 10,
        color: active && activeColor ? activeColor : '#fff',
        fontFamily: 'inherit',
        fontSize: 15,
        fontWeight: 500,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        transition: 'background 120ms',
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')
      }
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1 }}>{label}</span>
      {count !== null && (
        <span
          style={{
            color: 'rgba(255,255,255,0.55)',
            fontSize: 13,
            fontWeight: 500,
            minWidth: 24,
            textAlign: 'right',
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function ReactionBtn({
  icon,
  count,
  active,
  activeColor,
  onClick,
  'aria-label': ariaLabel,
  'aria-pressed': ariaPressed,
}: {
  icon: React.ReactNode;
  count?: string;
  active: boolean;
  activeColor?: string;
  onClick: () => void;
  'aria-label': string;
  'aria-pressed'?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        style={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(255,255,255,0.12)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          color: active && activeColor ? activeColor : '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 150ms',
          fontFamily: 'inherit',
        }}
      >
        {icon}
      </button>
      {count && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#fff',
            textShadow: '0 1px 3px rgba(0,0,0,0.4)',
          }}
        >
          {count}
        </div>
      )}
    </div>
  );
}

// ---- Shared helpers ----------------------------------------------------
